import { useSyncExternalStore } from 'react';

/**
 * The column order and widths a reader has dragged a table into.
 *
 * One `localStorage` key per table rather than one blob for all of them: the
 * writes stay small, two tables can never clobber each other, and a reader (or
 * a support engineer) can reset a single screen by deleting a single key.
 *
 * Only what the reader actually changed is stored. A column added in a later
 * release is absent from the saved order and therefore takes the position and
 * width the developer gave it — the same principle as `use-sidebar-groups.ts`,
 * which persists the collapsed set rather than the full state. Nothing is
 * written until somebody drags something.
 */
const STORAGE_PREFIX = 'bill-alps-table-prefs:';
/** Bumped only for a shape change; an unknown version reads as "nothing saved". */
const VERSION = 1;

export interface TablePreferences {
  /** `null` when the reader has never reordered this table. */
  order: string[] | null;
  /** Only the columns the reader resized, in px. */
  widths: Record<string, number>;
}

/** Shared so an untouched table hands `useSyncExternalStore` a stable snapshot. */
const EMPTY: TablePreferences = Object.freeze({
  order: null,
  widths: Object.freeze({}) as Record<string, number>,
});

/**
 * Parsed preferences per table id.
 *
 * The cache is the whole point: `useSyncExternalStore` compares snapshots by
 * identity, so parsing inside `getSnapshot` would hand React a fresh object on
 * every call and spin it in a render loop.
 */
const cache = new Map<string, TablePreferences>();
const listeners = new Set<() => void>();

/**
 * `null`, hand-edited junk, an older shape and wrong types all mean "nothing
 * saved" rather than a screen that fails to render. Column ids that no longer
 * exist and widths outside a column's bounds are dropped later, by
 * `reconcileColumnOrder` / `reconcileColumnWidths`, which know the columns.
 */
export function parseTablePreferences(raw: string | null): TablePreferences {
  if (!raw) return EMPTY;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return EMPTY;
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return EMPTY;
  }
  const record = parsed as Record<string, unknown>;
  if (record.v !== VERSION) return EMPTY;

  const order = Array.isArray(record.order)
    ? record.order.filter((id): id is string => typeof id === 'string')
    : null;

  const widths: Record<string, number> = {};
  if (
    record.widths !== null &&
    typeof record.widths === 'object' &&
    !Array.isArray(record.widths)
  ) {
    for (const [id, width] of Object.entries(
      record.widths as Record<string, unknown>,
    )) {
      if (typeof width === 'number' && Number.isFinite(width)) {
        widths[id] = width;
      }
    }
  }

  if (!order && Object.keys(widths).length === 0) return EMPTY;
  return { order, widths };
}

function storageKey(tableId: string) {
  return `${STORAGE_PREFIX}${tableId}`;
}

function read(tableId: string): TablePreferences {
  const cached = cache.get(tableId);
  if (cached) return cached;
  let preferences = EMPTY;
  try {
    preferences = parseTablePreferences(
      globalThis.window?.localStorage.getItem(storageKey(tableId)) ?? null,
    );
  } catch {
    // Blocked storage: the table renders its defaults, which is the point.
  }
  cache.set(tableId, preferences);
  return preferences;
}

function publish(tableId: string, next: TablePreferences) {
  cache.set(tableId, next);
  try {
    const isDefault = !next.order && Object.keys(next.widths).length === 0;
    if (isDefault) {
      globalThis.window?.localStorage.removeItem(storageKey(tableId));
    } else {
      globalThis.window?.localStorage.setItem(
        storageKey(tableId),
        JSON.stringify({ v: VERSION, order: next.order, widths: next.widths }),
      );
    }
  } catch {
    // Private-mode quota or blocked storage: the change still holds for the
    // rest of this page's life, it just won't survive a reload.
  }
  for (const listener of listeners) listener();
}

/** Records the order the reader dropped the columns into. */
export function setTableColumnOrder(tableId: string, order: string[]) {
  publish(tableId, { ...read(tableId), order });
}

/** Records one dragged column width; `null` restores the developer's width. */
export function setTableColumnWidth(
  tableId: string,
  columnId: string,
  width: number | null,
) {
  const current = read(tableId);
  const widths = { ...current.widths };
  if (width === null) delete widths[columnId];
  else widths[columnId] = width;
  publish(tableId, { ...current, widths });
}

/** Back to the order and widths the developer declared. */
export function resetTablePreferences(tableId: string) {
  publish(tableId, EMPTY);
}

/** True when nothing has been dragged — the reset control has nothing to undo. */
export function isDefaultTableLayout(preferences: TablePreferences) {
  return !preferences.order && Object.keys(preferences.widths).length === 0;
}

function subscribe(onChange: () => void) {
  listeners.add(onChange);
  if (listeners.size === 1) {
    globalThis.window?.addEventListener('storage', onStorage);
  }
  return () => {
    listeners.delete(onChange);
    if (listeners.size === 0) {
      globalThis.window?.removeEventListener('storage', onStorage);
    }
  };
}

/** Another tab moved a column: drop the stale parse and re-render. */
function onStorage(event: StorageEvent) {
  if (event.key === null) {
    cache.clear();
  } else if (event.key.startsWith(STORAGE_PREFIX)) {
    cache.delete(event.key.slice(STORAGE_PREFIX.length));
  } else {
    return;
  }
  for (const listener of listeners) listener();
}

/**
 * The stored layout for one table.
 *
 * `tableId` must be unique across the page — two tables sharing one would
 * overwrite each other's order on every drag.
 */
export function useTablePreferences(tableId?: string): TablePreferences {
  return useSyncExternalStore(
    subscribe,
    () => (tableId ? read(tableId) : EMPTY),
    () => EMPTY,
  );
}

/** Test seam: drops the parsed cache so the next read hits storage again. */
export function clearTablePreferencesCache() {
  cache.clear();
}
